/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/adminProcess.scss';

import React, {KeyboardEventHandler, MouseEventHandler, PropsWithChildren} from 'react';
import {useTranslation} from 'react-i18next';

import {AwesomeIcon, ChangeFunction, Checkbox, ElementPosition, Loader, voidFunction} from '@dvrd/dvr-controls';

import User from '../../../models/userModel';

import Account from '../../../models/accountModel';

import ProcessStatus from '../../../models/processStatusModel';

import {BolmateButton, BolmateInput} from '../../controls';

import {ProcessingStatus, SubscriptionStatus} from '../../../util/interfaces';

import {getSubscriptionLabel} from '../../../util/subscriptionUtil';

import classNames from 'classnames';

interface Props {
    onChangeSearch: ChangeFunction<string>;
    onChangePage: (nextPage: number) => MouseEventHandler;
    onEnterSearch: KeyboardEventHandler;
    onOpenFilter: MouseEventHandler;
    onSelectUser: (user: User) => MouseEventHandler;
    onSelectAccount: (account: Account) => MouseEventHandler;
    onDeleteProcess: (process: ProcessStatus) => MouseEventHandler;
    users: Array<User>;
    accounts: Array<Account>;
    selectedUser: User | null;
    selectedAccount: Account | null;
    loadingUsers: boolean;
    loadingProcesses: boolean;
    loadingAccounts: boolean;
    page: number;
    search: string;
    processes: Array<ProcessStatus>;
    limit: number;
}

export default function AdminProcess(props: PropsWithChildren<Props>) {
    const {t} = useTranslation();
    const {
        onChangeSearch,
        search,
        onDeleteProcess,
        processes,
        loadingProcesses,
        loadingUsers,
        selectedUser,
        onSelectAccount,
        onSelectUser,
        selectedAccount,
        accounts,
        users,
        loadingAccounts,
        page,
        onChangePage,
        limit,
        onOpenFilter,
        onEnterSearch,
        children
    } = props;

    function renderHeader() {
        return (
            <div className='header-container'>
                <label className='header-title'>{t('admin.adminProcess.users', 'Gebruikers')}</label>
                <BolmateInput
                    value={search}
                    onChange={onChangeSearch}
                    label={t('admin.adminProcess.search', 'Zoeken')}
                    ornaments={{
                        element: <AwesomeIcon name='search' />,
                        placement: ElementPosition.RIGHT
                    }}
                    className='search-field'
                    onEnter={onEnterSearch}
                    autoFocus
                    unControlled
                />
                <BolmateButton
                    onClick={onOpenFilter}
                    label={t('admin.adminProcess.filter', 'Filter')}
                    className='btn-filter'
                    secondary
                />
            </div>
        );
    }

    function renderUsers() {
        return (
            <div className='users-container'>
                <Loader active={loadingUsers} />
                <div className='users-table'>
                    <div className='row default-table-row head'>
                        <div />
                        <label>{t('admin.adminProcess.col.id', 'ID')}</label>
                        <label>{t('admin.adminProcess.col.name', 'NAAM')}</label>
                        <label>{t('admin.adminProcess.col.email', 'E-MAILADRES')}</label>
                        <label>{t('admin.adminProcess.col.subscription', 'ABONNEMENT')}</label>
                    </div>
                    {users.map(renderUser)}
                </div>
                {renderPagination()}
            </div>
        );
    }

    function renderUser(user: User, index: number) {
        const selected = user.id === selectedUser?.id;
        const subStatus = user.subscription?.status ?? SubscriptionStatus.INACTIVE;
        return (
            <div
                key={index}
                className={classNames('row default-table-row click', selected && 'selected')}
                onClick={onSelectUser(user)}
            >
                <Checkbox onCheck={voidFunction} checked={selected} />
                <label>{user.id}</label>
                <label>{user.fullName}</label>
                <label>{user.email}</label>
                <label>{getSubscriptionLabel(subStatus)}</label>
            </div>
        );
    }

    function renderPagination() {
        const prevDisabled = page === 0;
        const nextDisabled = users.length < limit;
        return (
            <div className='pagination'>
                <AwesomeIcon
                    name='chevron-left'
                    className={classNames('pager', prevDisabled && 'disabled')}
                    onClick={prevDisabled ? voidFunction : onChangePage(page - 1)}
                />
                <AwesomeIcon
                    name='chevron-right'
                    className={classNames('pager', nextDisabled && 'disabled')}
                    onClick={nextDisabled ? voidFunction : onChangePage(page + 1)}
                />
            </div>
        );
    }

    function renderAccounts() {
        return (
            <section className='accounts-container'>
                <Loader active={loadingAccounts} />
                <label className='section-title'>{t('admin.adminProcess.linkedAccounts', 'Gekoppelde accounts')}</label>
                <div className='row default-table-row head'>
                    <div />
                    <label>{t('admin.adminProcess.col.id', 'ID')}</label>
                    <label>{t('admin.adminProcess.col.companyName', 'BEDRIJFSNAAM')}</label>
                    <label>{t('admin.adminProcess.col.email', 'E-MAILADRES')}</label>
                </div>
                {accounts.map(renderAccount)}
                {!accounts.length && (
                    <div className='row default-table-row empty'>
                        <label className='empty-label'>
                            {t('admin.adminProcess.selectUser', 'Selecteer een gebruiker')}
                        </label>
                    </div>
                )}
            </section>
        );
    }

    function renderAccount(account: Account, index: number) {
        const selected = account.id === selectedAccount?.id;
        return (
            <div
                className={classNames('row default-table-row click', selected && 'selected')}
                key={index}
                onClick={onSelectAccount(account)}
            >
                <Checkbox onCheck={voidFunction} checked={selected} />
                <label>{account.id}</label>
                <label>{account.selectablePlatformLabel}</label>
                <label>{account.email}</label>
            </div>
        );
    }

    function renderProcesses() {
        return (
            <section className='processes-container'>
                <Loader active={loadingProcesses} />
                <label className='section-title'>{t('admin.adminProcess.imports', 'Imports')}</label>
                <div className='processes'>
                    <div className='row default-table-row head'>
                        <label>{t('admin.adminProcess.col.startedAt', 'GESTART OP')}</label>
                        <label>{t('admin.adminProcess.col.account', 'ACCOUNT')}</label>
                        <label>{t('admin.adminProcess.col.status', 'STATUS')}</label>
                        <div />
                    </div>
                    {processes.map(renderProcess)}
                    {!processes.length && (
                        <div className='row default-table-row empty'>
                            <label className='empty-label'>
                                {t('admin.adminProcess.noProcesses', 'Er zijn geen processen gevonden')}
                            </label>
                        </div>
                    )}
                </div>
            </section>
        );
    }

    function renderProcess(process: ProcessStatus, index: number) {
        const canDelete = [ProcessingStatus.FINISHED, ProcessingStatus.ERROR].includes(process.status);
        let color: string;
        if (process.status === ProcessingStatus.FINISHED) color = 'green';
        else if (process.status === ProcessingStatus.ERROR) color = 'red';
        else if (process.status === ProcessingStatus.WAITING) color = 'orange';
        else color = 'blue';
        return (
            <div key={index} className='row default-table-row'>
                <label>{process.createdAtMoment.format('DD-MM-YYYY HH:mm')}</label>
                <label>{process.data['companyName']}</label>
                <label>
                    <span className={color}>{process.translatedStatus}</span> - {process.data['message']}
                </label>
                {canDelete && (
                    <AwesomeIcon name='trash-alt' className='btn-delete' onClick={onDeleteProcess(process)} />
                )}
            </div>
        );
    }

    return (
        <div className='admin-process'>
            <section>
                {renderHeader()}
                {renderUsers()}
            </section>
            {renderAccounts()}
            {children}
            {renderProcesses()}
        </div>
    );
}
